import { isNull } from 'lodash/fp';
import React, { useCallback, useEffect } from 'react';
import { Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { TextItem } from 'react-pdf/dist/Page';
import { useIntersectionObserver } from 'usehooks-ts';

import { useAppSelector } from '../../../hooks/react-redux';
import { getPageHighlightInfo, PDFPageHighlights, PdfPageText, PdfSearchResults } from './store';

const observerConfig = {
    threshold: [0.2, 0.8]
};

interface PageWithObserver {
    pageNumber: number;
    setPageVisibility: (pageNumber: number, isIntersecting: boolean) => void;
    pagesWidth: number;
    width: number;
    setRef: (element: HTMLDivElement | null, pageNumber: number) => void;
    scrollToThumbnail: (pageNumber: number) => void;
    setPdfPagesText: (pageText: PdfPageText) => void;
    searchTermHighlights?: PDFPageHighlights;
    selectedSearchResult: PdfSearchResults | null;
}

export const PageWithObserver: React.FC<PageWithObserver> = ({ pageNumber, setPageVisibility, pagesWidth, width, setRef, scrollToThumbnail, setPdfPagesText, searchTermHighlights, selectedSearchResult }) => {
    const { ref, isIntersecting } = useIntersectionObserver(observerConfig);
    const pageHighlightInfo = useAppSelector(getPageHighlightInfo);

    const customTextRenderer = useCallback((str: string, itemIndex: number, pageNumber: number) => {
        if (pageNumber === pageHighlightInfo?.page && pageHighlightInfo.highlightIndexes.length > 0 && pageHighlightInfo.highlightIndexes.includes(itemIndex)) {
            return `<mark style="background: #f5ff65;" id='highlight-ref-${pageNumber}-${itemIndex}'>${str}</mark>`;
        }
        if (!isNull(selectedSearchResult) && pageNumber === selectedSearchResult.page && selectedSearchResult.indexes.includes(itemIndex)) {
            return `<mark style="background: #eef370;" id='search-highlight-ref-${pageNumber}-${itemIndex}'>${str}</mark>`;
        }
        if (pageNumber === searchTermHighlights?.page && searchTermHighlights.highlightIndexes.length > 0 && searchTermHighlights.highlightIndexes.includes(itemIndex)) {
            return `<mark style="background: #f5ff65;" id='search-highlight-ref-${pageNumber}-${itemIndex}'>${str}</mark>`;
        }
        return str;
    }, [pageHighlightInfo, searchTermHighlights, selectedSearchResult]);

    const onGetTextSuccess = useCallback((textItems: TextItem[]) => setPdfPagesText({ pageNumber, textItems }), [setPdfPagesText, pageNumber]);

    useEffect(() => {
        setPageVisibility(pageNumber, isIntersecting);
    }, [pageNumber, setPageVisibility, isIntersecting]);

    return (
        <div ref={ref} id={`pdf-page-${pageNumber}`}>
            <Page
                key={`page_${pageNumber}`}
                pageNumber={pageNumber}
                width={width * pagesWidth}
                loading={null}
                inputRef={element => setRef(element, pageNumber)}
                onClick={() => scrollToThumbnail(pageNumber)}
                onGetTextSuccess={({ items }) => onGetTextSuccess(items as TextItem[])}
                customTextRenderer={({ str, itemIndex, pageNumber }) => customTextRenderer(str, itemIndex, pageNumber)}
            />
        </div>
    );
};
